import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export const inputClass = 'w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm';
export const selectClass = 'px-3 py-2 bg-background border border-border rounded text-foreground text-sm';
export const labelClass = 'block text-sm font-medium text-foreground mb-1';
export const btnPrimary = 'inline-flex items-center gap-1 bg-primary text-primary-foreground px-3 py-2 rounded hover:bg-primary/90 disabled:opacity-50 text-sm transition-colors';
export const btnSecondary = 'inline-flex items-center gap-1 bg-secondary text-foreground border border-border px-3 py-2 rounded hover:bg-secondary/80 disabled:opacity-50 text-sm transition-colors';
export const btnDanger = 'inline-flex items-center gap-1 text-destructive px-3 py-2 rounded border border-destructive hover:bg-destructive/10 disabled:opacity-50 text-sm transition-colors';
export const btnLink = 'text-primary hover:text-primary/80 text-sm';

export function Spinner() {
  return (
    <div className="text-center py-8" role="status" aria-label="Loading">
      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
    </div>
  );
}

export function EmptyState({ children }) {
  return <p className="text-center py-8 text-muted-foreground text-sm">{children}</p>;
}

export function Panel({ children, className = '' }) {
  return <div className={cn('bg-card rounded-lg border border-border p-4', className)}>{children}</div>;
}

export function Toolbar({ children }) {
  return <div className="flex flex-wrap items-center gap-2 mb-4">{children}</div>;
}

export function SearchInput({ value, onChange, placeholder, label }) {
  const [text, setText] = useState(value || '');
  const reported = useRef(value || '');

  useEffect(() => {
    if ((value || '') === reported.current) return;
    reported.current = value || '';
    setText(value || '');
  }, [value]);

  useEffect(() => {
    if (text === reported.current) return undefined;
    const timer = setTimeout(() => {
      reported.current = text;
      onChange(text);
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div className="relative flex-1 min-w-48">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        className={`${inputClass} pl-9`}
      />
    </div>
  );
}

export function SegmentedControl({ options, value, onChange, label }) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border border-border overflow-hidden">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`px-3 py-2 text-sm transition-colors ${
            value === option.value ? 'bg-secondary text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {option.label}
          {option.count !== undefined && <span className="ml-1 text-muted-foreground">· {option.count}</span>}
        </button>
      ))}
    </div>
  );
}

const PRIORITY_CLASSES = {
  urgent: 'text-priorityUrgent border-priorityUrgent',
  high: 'text-priorityHigh border-priorityHigh',
  medium: 'text-priorityMedium border-priorityMedium',
  low: 'text-priorityLow border-priorityLow'
};

export function Badge({ children, className = 'text-muted-foreground border-border' }) {
  return <span className={cn('inline-block text-xs px-2 rounded-full border whitespace-nowrap capitalize', className)}>{children}</span>;
}

export function PriorityBadge({ priority }) {
  return <Badge className={PRIORITY_CLASSES[priority] || undefined}>{priority}</Badge>;
}

export function StatusBadge({ status }) {
  return <Badge className={status === 'open' ? 'text-primary border-primary' : undefined}>{status}</Badge>;
}

const HIDE_BELOW = { md: 'hidden md:table-cell', lg: 'hidden lg:table-cell', xl: 'hidden xl:table-cell' };

export function DataTable({ columns, rows, rowKey = '_id', selectedId, onRowClick, emptyMessage, label }) {
  if (rows.length === 0) return <EmptyState>{emptyMessage}</EmptyState>;
  const hide = (column) => HIDE_BELOW[column.hideBelow] || '';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label={label}>
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            {columns.map((column) => (
              <th key={column.key} scope="col" className={`px-3 py-2 font-medium whitespace-nowrap ${hide(column)}`}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = row[rowKey];
            const selected = selectedId !== undefined && String(selectedId) === String(id);
            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter') onRowClick(row); } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-selected={onRowClick ? selected : undefined}
                className={`border-b border-border last:border-b-0 ${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''} ${selected ? 'bg-secondary' : ''}`}
              >
                {columns.map((column) => (
                  <td key={column.key} className={`px-3 py-3 align-middle ${column.className || ''} ${hide(column)}`}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function pageWindow(page, pages) {
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  return Array.from({ length: Math.min(5, pages) }, (_, i) => start + i);
}

export function Pagination({ page, pages, total, limit, onChange }) {
  if (!total) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <nav aria-label="Pagination" className="flex flex-wrap items-center gap-2 pt-4 text-sm">
      <span className="text-muted-foreground mr-auto">Showing {from}–{to} of {total}</span>
      {pages > 1 && (
        <>
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)} className="p-1 rounded border border-border disabled:opacity-40 hover:bg-muted">
            <ChevronLeft className="h-4 w-4" />
          </button>
          {pageWindow(page, pages).map((n) => (
            <button
              key={n}
              type="button"
              aria-current={n === page ? 'page' : undefined}
              onClick={() => onChange(n)}
              className={`min-w-8 px-2 py-1 rounded border ${n === page ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
            >
              {n}
            </button>
          ))}
          <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => onChange(page + 1)} className="p-1 rounded border border-border disabled:opacity-40 hover:bg-muted">
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </nav>
  );
}

// On large screens the drawer sits beside the list; below that it covers the screen like a sheet.
export function Drawer({ title, subtitle, badges, onClose, children }) {
  return (
    <aside
      aria-label={typeof title === 'string' ? title : 'Details'}
      className="fixed inset-0 z-40 overflow-y-auto bg-background p-4 pb-24 lg:static lg:z-auto lg:bg-card lg:rounded-lg lg:border lg:border-border lg:p-5 lg:pb-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="min-w-0 flex-1">
          {badges && <div className="flex flex-wrap items-center gap-2 mb-1">{badges}</div>}
          <h2 className="text-lg font-bold text-foreground break-words">{title}</h2>
          {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
          <X className="h-5 w-5" />
        </button>
      </div>
      {children}
    </aside>
  );
}

export function ListLayout({ drawer, wideDrawer = false, children }) {
  if (!drawer) return <div>{children}</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
      <div className={wideDrawer ? 'lg:col-span-1' : 'lg:col-span-2'}>{children}</div>
      <div className={wideDrawer ? 'lg:col-span-2' : 'lg:col-span-1'}>{drawer}</div>
    </div>
  );
}
